import apiClient from '../utils/apiClient';
import { InventoryItem, Card, Product } from 'shared/models';

const API_URL = '/api/inventory';

const inventoryService = {
  // Get user inventory with pagination
  getUserInventoryByPage: async (page = 1, limit = 20, filters = {}) => {
    const params = {
      page,
      limit,
      ...filters
    };
    
    const response = await apiClient.get(API_URL, { 
      params
    });
    
    return {
      inventoryItems: (response.data.inventoryItems || []).map(item => new InventoryItem(item)),
      currentPage: response.data.currentPage || page,
      totalPages: response.data.totalPages || 1,
      totalItems: response.data.totalItems || 0
    };
  },
  
  // Get products in user's collection with pagination
  getUserCollectionProductsByPage: async (page = 1, limit = 20, sortBy = null) => {
    const params = {
      page,
      limit
    };
    
    // Add optional sort parameter if provided
    if (sortBy) {
      params.sortBy = JSON.stringify(sortBy);
    }
    
    const response = await apiClient.get(`${API_URL}/products`, { 
      params
    });
    
    return {
      products: (response.data.products || []).map(product => new Product(product)),
      currentPage: response.data.currentPage || page,
      totalPages: response.data.totalPages || 1,
      totalProducts: response.data.totalProducts || 0
    };
  },
  
  // Get products in a specific user's collection
  getUserCollectionProducts: async (userId, page = 1, limit = 20, sortBy = null) => {
    if (!userId) {
      throw new Error('User ID is required');
    }
    
    const params = {
      page,
      limit
    };
    
    // Add optional sort parameter if provided
    if (sortBy) {
      params.sortBy = JSON.stringify(sortBy);
    }
    
    const response = await apiClient.get(`${API_URL}/user/${userId}/products`, { 
      params
    });
    
    return {
      products: (response.data.products || []).map(product => new Product(product)),
      currentPage: response.data.currentPage || page,
      totalPages: response.data.totalPages || 1,
      totalProducts: response.data.totalProducts || 0,
      username: response.data.username
    };
  },
  
  // Get inventory statistics for a user
  getUserInventoryStats: async (userId) => {
    if (!userId) {
      throw new Error('User ID is required');
    }
    
    const response = await apiClient.get(`${API_URL}/stats/user/${userId}`);
    
    return {
      totalCards: response.data.totalCards || 0,
      totalProducts: response.data.totalProducts || 0,
      totalInsertSets: response.data.totalInsertSets || 0
    };
  },
  
  // Get user inventory items for a specific insert set
  getUserInventoryByInsertSet: async (insertSet) => {
    const params = {
      insertSetId: insertSet.id
    };
    
    const response = await apiClient.get(`${API_URL}/insert-set`, { 
      params
    });
    
    return {
      inventoryItems: (response.data.inventoryItems || []).map(item => new InventoryItem(item)),
      totalItems: response.data.totalItems || 0
    };
  },
  
  // Get user inventory for a specific card
  getUserInventoryByCard: async (cardId) => {
    const response = await apiClient.get(`${API_URL}/card/${cardId}`);
    
    return response.data.inventoryItem ? new InventoryItem(response.data.inventoryItem) : null;
  },
  
  // Get inventory item for a specific card and user
  getUserInventoryByCardAndUser: async (cardId, userId) => {
    if (!cardId) {
      throw new Error('Card ID is required');
    }
    
    if (!userId) {
      throw new Error('User ID is required');
    }
    
    const response = await apiClient.get(`${API_URL}/card/${cardId}/user/${userId}`);
    
    return response.data.inventoryItem ? new InventoryItem(response.data.inventoryItem) : null;
  },
  
  // Get public inventory of a user with pagination
  getPublicUserInventoryByPage: async (userId, page = 1, limit = 20) => {
    const params = {
      page,
      limit
    };
    
    const response = await apiClient.get(`${API_URL}/user/${userId}/public`, { params });
    
    return {
      inventoryItems: (response.data.inventoryItems || []).map(item => new InventoryItem(item)),
      currentPage: response.data.currentPage || page,
      totalPages: response.data.totalPages || 1,
      totalItems: response.data.totalItems || 0
    };
  },
  
  // Add a card to inventory with a specified quantity (default 1)
  addToInventory: async (card, quantity = 1) => {
    if (!card || !card.id) {
      throw new Error('Invalid card provided');
    }
    
    const response = await apiClient.post(API_URL, {
      card: card.id,
      quantity: quantity
    });
    
    return response.data;
  },
  
  // Remove a card from inventory by decrementing quantity
  removeFromInventory: async (card, quantity = 1) => {
    if (!card || !card.id) {
      throw new Error('Invalid card provided');
    }
    
    const response = await apiClient.post(API_URL, {
      card: card.id,
      quantity: -quantity // Negative quantity to remove
    });
    
    return response.data;
  },
  
  // Update inventory quantity for a specific card
  updateInventory: async (card, quantity) => {
    if (!card || !card.id) {
      throw new Error('Invalid card provided');
    }
    
    if (quantity < 0) {
      throw new Error('Quantity cannot be negative');
    }
    
    const response = await apiClient.put(`${API_URL}/card/${card.id}`, {
      quantity: quantity
    });
    
    return new InventoryItem(response.data.inventoryItem || response.data);
  },
  
  // Toggle "for trade" status
  toggleInventoryItemForTrade: async (inventoryItem) => {
    const response = await apiClient.put(`${API_URL}/${inventoryItem.id}/toggle-trade`, {});
    
    return new InventoryItem(response.data.inventoryItem || response.data);
  },
  
  // Toggle "for sale" status
  toggleInventoryItemForSale: async (inventoryItem) => {
    const response = await apiClient.put(`${API_URL}/${inventoryItem.id}/toggle-sale`, {});
    
    return new InventoryItem(response.data.inventoryItem || response.data);
  },
  
  // Add all cards from an insert set to inventory
  addInsertSetToInventory: async (insertSetId) => {
    if (!insertSetId) {
      throw new Error('Insert Set ID is required');
    }
    
    const response = await apiClient.post(`${API_URL}/insert-set/${insertSetId}`, {});
    
    return response.data;
  }
};

export default inventoryService;